/**
 * Camp Submissions API Utility Functions
 * Handles all API calls for camp submissions
 */

import { apiRequest } from '../config/api';

/**
 * List camp submissions with optional filters
 * @param {Object} params - Query parameters
 * @param {string} params.q - Search query
 * @param {number} params.limit - Number of items per page
 * @param {number} params.offset - Offset for pagination
 * @param {string} params.sort - Sort field and order (e.g., "created_at.desc")
 * @returns {Promise<Object>} Response with items, total, limit, offset
 */
export const listCampSubmissions = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.q) queryParams.append('q', params.q);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.view_as_admin) queryParams.append('view_as_admin', params.view_as_admin);
  
  const queryString = queryParams.toString();
  const url = `get_camp_submissions.php${queryString ? '?' + queryString : ''}`;
  
  const response = await apiRequest(url);
  return response;
};

/**
 * Create a new camp submission
 * @param {Object} payload - Camp submission data
 * @param {string} payload.name - Name (required)
 * @param {string} payload.dob - Date of birth (optional)
 * @param {string} payload.email - Email (optional)
 * @param {string} payload.phone - Phone (optional)
 * @param {string} payload.institution - Institution (optional)
 * @param {string} payload.institution_type - Institution type (optional)
 * @param {string} payload.comments - Comments (optional)
 * @returns {Promise<Object>} Response with success status and id
 */
export const createCampSubmission = async (payload) => {
  const response = await apiRequest('add_camp_submission.php', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  return response;
};

/**
 * Update an existing camp submission
 * @param {number} id - Submission ID
 * @param {Object} payload - Updated camp submission data
 * @returns {Promise<Object>} Response with success status
 */
export const updateCampSubmission = async (id, payload) => {
  const response = await apiRequest('update_camp_submission.php', {
    method: 'PUT',
    body: JSON.stringify({ id, ...payload })
  });
  return response;
};

/**
 * Delete a camp submission
 * @param {number} id - Submission ID
 * @returns {Promise<Object>} Response with success status
 */
export const deleteCampSubmission = async (id) => {
  const response = await apiRequest(`delete_camp_submission.php?id=${id}`, {
    method: 'DELETE'
  });
  return response;
};

/**
 * List camp submission audit logs
 * @param {Object} params - Query parameters
 * @param {number} params.row_id - Filter by specific submission ID (optional)
 * @param {string} params.action - Filter by action: INSERT, UPDATE, DELETE (optional)
 * @param {number} params.limit - Number of items per page
 * @param {number} params.offset - Offset for pagination
 * @returns {Promise<Object>} Response with items, total, limit, offset
 */
export const listCampSubmissionLogs = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.row_id) queryParams.append('row_id', params.row_id);
  if (params.action) queryParams.append('action', params.action);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.view_as_admin) queryParams.append('view_as_admin', params.view_as_admin);
  
  const queryString = queryParams.toString();
  const url = `get_camp_submission_logs.php${queryString ? '?' + queryString : ''}`;
  
  const response = await apiRequest(url);
  return response;
};

/**
 * Get audit logs for a specific camp submission
 * @param {number} rowId - Submission ID
 * @param {Object} params - Additional query parameters
 * @returns {Promise<Object>} Response with items, total, limit, offset
 */
export const getCampSubmissionLogsById = async (rowId, params = {}) => {
  return listCampSubmissionLogs({ row_id: rowId, ...params });
};

